<script>
    import {
        CardLayout,
        NotificationModal,
    } from "../../../../../Components/Common/index.svelte";

    import {
        amaUnitValue,
        asaAllItemsList,
        mbsAllItemsList,
    } from "../../../../../stores";

    import { apiMethods } from "../../../../../Services/index.svelte";

    import {
        getAmountInCurrency,
        getBillingType,
        getBoolean,
        getFormatDateAndTime,
        getOOPType,
        isObjectEmpty,
        showError,
        showException,
    } from "../../../../../Utils/utilFunctions.svelte";

    import {
        getDescriptionByTime,
        getTimeItemsByUnits,
        getTimeUnits,
        updateProcedureItems as updateAsaProcedureItems,
    } from "../../../../PatientEncounter/helpers/asaHelper.svelte";

    import {
        convertTimeRangeInMin,
        convertTitleToMin,
        updateProcedureItems as updateMbsProcedureItems,
    } from ".../../../../PatientEncounter/helpers/mbsHelper.svelte";

    import {
        syncInsuranceData,
        updateEncounterLatestBillingData,
    } from ".../../../../PatientEncounter/helpers/encounterHelper.svelte";

    import {
        addIcon,
        editIcon,
    } from "../../../../../Utils/utilConstants.svelte";

    import { PatientQuoteBillingDetailsModal as ModifyBillingDetailsModal } from "./index.svelte";
    import { getPublicHolidays } from "../../../../../Utils/DataLoaders.svelte";

    export let patientEncounterDetails = {};
    export let latestObjDetails = {};
    export let itemsData = [];
    export let associateArr = [];
    export let type = "";
    export let reloadBillingDetailsAndUpdateDefaultItems = () => {};
    export let reloadBillingItemsWhenTimeItemAdded = () => {};
    export let reloadBillingDetails = () => {};
    export let regenerateQuote = () => {};
    export let quoteItemsRef;

    $: if (isObjectEmpty(latestObjDetails)) {
        btnOptions[0].iFont = addIcon;
        btnOptions[0].btnText = "Add";
    } else {
        btnOptions[0].iFont = editIcon;
        btnOptions[0].btnText = "Edit";
    }

    let isEditDetailsModalOpen = false,
        isUpdatingDetails = false,
        isTimeItemAdded = false,
        isProcedureItemAdded = false;

    let isSyncAssociateQuoteOrInvoiceModalOpen = false,
        isUpdatingAssociateQuoteOrInvoice = false;

    const btnOptions = [
        {
            iconName: "editIcon",
            isHidden: true,
            iconFunction: () => _openEditDetailModal(),
            hasTooltip: false,
        },
    ];

    const confirmUpdateLatestDetails = (
        hasBillingTypeFiguresChanged = false,
        ...args
    ) => {
        // if (hasBillingTypeFiguresChanged) _openConfirmActionModal(...args);
        // else updateLatestDetails(...args);
        updateLatestDetails(...args);
    };

    const updateLatestDetails = async (
        modalBillingData,
        insuranceObj,
        hasItemTypeChanged = false,
        hasInsuranceTypeChange = false,
    ) => {
        const tempInsuranceData = syncInsuranceData(
            modalBillingData,
            insuranceObj,
            patientEncounterDetails,
        );

        const { modalData, encounterData } = tempInsuranceData;

        modalBillingData = modalData;

        patientEncounterDetails = encounterData;

        modalBillingData = await _syncBillingDataItemsByDate(modalBillingData);

        modalBillingData = await _syncProcedureItems(modalBillingData);

        _syncBillingData(modalBillingData);

        if (hasItemTypeChanged) _syncBillingDataItems(modalBillingData);

        if (hasInsuranceTypeChange)
            _syncBillingSelectorDetails(modalBillingData);

        patientEncounterDetails = updateEncounterLatestBillingData(
            patientEncounterDetails,
        );

        // re-calculate whenever latest billing data update
        if (modalBillingData.id && !hasItemTypeChanged)
            await regenerateQuote(modalBillingData, true, type);

        isUpdatingDetails = true;

        await apiMethods.putMethod
            .updatePatientEncounter(patientEncounterDetails, false, "Update fee estimate latest billing details")
            .then(
                async (res) =>
                    await _handleUpdateBillingData(
                        res,
                        hasItemTypeChanged,
                        modalBillingData,
                    ),
            )
            .catch((err) => showException(err));

        quoteItemsRef.syncDisplayItems();

        isUpdatingDetails = false;

        _closeEditDetailModal();
    };

    const syncAssociateQuoteOrInvoice = async () => {
        isUpdatingAssociateQuoteOrInvoice = true;

        await regenerateQuote(latestObjDetails, true, type);

        _closeSyncAssociateQuoteOrInvoiceModal();

        isUpdatingAssociateQuoteOrInvoice = false;
    };

    // HELPER FUNCTIONS
    const _syncBillingData = (data) => {
        if (data.id)
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (bd.id == data.id) return { ...bd, ...data };
                    return bd;
                });
        else {
            data.isLatestQuoteData = Boolean(true);

            patientEncounterDetails.billingData = [{ ...data }];
        }
    };

    const _syncBillingDataItemsByDate = async (data) => {
        let res;

        const { useAsaCodes } = data;

        const { typeOfBill } = patientEncounterDetails;

        // ONLY Anaesthesia encounter support time code
        if (typeOfBill != 0) return data;

        if (useAsaCodes) res = _syncAsaTimeItem(data);
        else res = await _syncMbsTimeItem(data);

        return res;
    };

    const _syncProcedureItems = async (data) => {
        let res;

        const { useAsaCodes, startDate, endDate } = data;

        const { typeOfBill, neuraxialType } = patientEncounterDetails;

        if (typeOfBill != 2) return data;

        if (neuraxialType && (!startDate || !endDate)) return data;

        if (useAsaCodes) res = await _syncAsaProcedureItem(data);
        else res = await _syncMbsProcedureItem(data);

        return res;
    };

    const _syncMbsTimeItem = async (data) => {
        const { startDate, endDate, mbsData } = data;

        if (!startDate || !endDate) return { ...data };

        const timeDiff = convertTimeRangeInMin(startDate, endDate);

        let timeItemsList = [];

        if (!$mbsAllItemsList.length)
            await apiMethods.getMethod
                .getMBSItems(0, 999999, {
                    "filter[infoType]": "Time",
                })
                .then((res) => (timeItemsList = [...res.results]));
        else
            timeItemsList = $mbsAllItemsList.filter(
                (item) => item.infoType == "Time",
            );

        const targetTimeItem = timeItemsList.find((item) => {
            const timeRange = convertTitleToMin(item.infoShortTitle);
            return timeDiff >= timeRange[0] && timeDiff <= timeRange[1];
        });

        if (!targetTimeItem) return { ...data };

        if (!mbsData) {
            isTimeItemAdded = true;
            return { ...data, mbsData: [{ ...targetTimeItem }] };
        }

        const currentTimeData = mbsData.find((item) => item.infoType == "Time");

        if (
            currentTimeData &&
            currentTimeData.infoShortTitle == targetTimeItem.infoShortTitle
        )
            return { ...data };

        const newMbsData = mbsData.filter((item) => item.infoType != "Time");

        isTimeItemAdded = true;

        return { ...data, mbsData: [...newMbsData, { ...targetTimeItem }] };
    };

    const _syncAsaTimeItem = (data) => {
        const { startDate, endDate } = data;

        if (!startDate || !endDate) return { ...data };

        const timeDiff = convertTimeRangeInMin(startDate, endDate);
        const targetTimeItemIndex = _getTimeItemIndex(timeDiff);
        const targetTimeItemDescription =
            getDescriptionByTime(targetTimeItemIndex);

        const updateTimeItem = {
            units: getTimeUnits(targetTimeItemIndex),
            feeCharged: getTimeUnits(targetTimeItemIndex) * $amaUnitValue,
            encounterItemDescription: targetTimeItemDescription,
            itemCode: "Time",
        };

        const timeItem = itemsData.find((item) => item.itemCode == "Time");

        if (!timeItem) {
            isTimeItemAdded = true;
            return { ...data, asaTimeData: [{ ...updateTimeItem }] };
        } else {
            const timeItemIndex = getTimeItemsByUnits(itemsData);

            const timeItemDescription = getDescriptionByTime(timeItemIndex);

            if (timeItemDescription == targetTimeItemDescription)
                return { ...data };

            isTimeItemAdded = true;

            return { ...data, asaTimeData: [{ ...updateTimeItem }] };
        }
    };

    const _syncMbsProcedureItem = async (data) => {
        const { mbsData, startDate, endDate } = data;
        const { neuraxialType, typeOfBill, scheduledDate } =
            patientEncounterDetails;
        const targetProcedureItem =
            neuraxialType == 1
                ? { key: 1, value: "Insertion" }
                : { key: 2, value: "Top up | Revision" };
        const holidayObj = {
            "filter[year]": scheduledDate
                ? new Date(scheduledDate).getFullYear()
                : new Date().getFullYear(),
        };
        const itemProps = {
            startDate,
            endDate,
            billingType: typeOfBill,
        };

        let res = mbsData ?? [],
            procedureItems = [],
            holidaysInState = [];

        if (!$mbsAllItemsList.length)
            await apiMethods.getMethod
                .getMBSItems(0, 999999, {
                    "filter[infoType]": "AdditionalProcedures",
                })
                .then((res) => (procedureItems = [...res.results]));
        else
            procedureItems = $mbsAllItemsList.filter(
                (item) => item.infoType == "AdditionalProcedures",
            );

        if (data.patientInsuranceState)
            holidayObj["filter[state]"] = data.patientInsuranceState;

        await getPublicHolidays(0, 999999, holidayObj).then(
            (res) => (holidaysInState = [...res.results] ?? []),
        );

        res = await updateMbsProcedureItems(
            targetProcedureItem,
            res,
            itemProps,
            procedureItems,
            holidaysInState,
        );

        isProcedureItemAdded = true;

        return { ...data, mbsData: [...res] };
    };

    const _syncAsaProcedureItem = async (data) => {
        const { asaData, startDate, endDate } = data;
        const { neuraxialType, typeOfBill } = patientEncounterDetails;
        const targetProcedureItem =
            neuraxialType == 1
                ? { key: 1, value: "Insertion" }
                : { key: 2, value: "Top up | Revision" };
        const itemProps = {
            startDate,
            endDate,
            billingType: typeOfBill,
        };

        let res = asaData ?? [],
            procedureItems = [];

        if (!$asaAllItemsList.length)
            await apiMethods.getMethod
                .getASAItems(0, 999999, {
                    "filter[infoType]": "AdditionalProcedures",
                })
                .then((res) => (procedureItems = [...res.results]));
        else
            procedureItems = $asaAllItemsList.filter(
                (item) => item.infoType == "AdditionalProcedures",
            );

        res = await updateAsaProcedureItems(
            targetProcedureItem,
            res,
            itemProps,
            procedureItems,
        );

        isProcedureItemAdded = true;

        return { ...data, asaData: [...res] };
    };

    const _getTimeItemIndex = (timeDiff) => {
        if (timeDiff <= 120) return Math.ceil(timeDiff / 15) - 1;
        else if (timeDiff <= 240)
            return 8 + Math.ceil((timeDiff - 120) / 10) - 1;
        else return 20 + Math.ceil((timeDiff - 240) / 5) - 1;
    };

    const _syncBillingDataItems = (data) => {
        patientEncounterDetails.billingData =
            patientEncounterDetails.billingData.map((bd) => {
                if (bd.id == data.id && bd.isLatestQuoteData)
                    return {
                        ...bd,
                        asaData: [],
                        mbsData: [],
                        asaTimeData: null,
                        actualGap: Number(0),
                        rebate: Number(0),
                        totalFee: Number(0),
                        useAsaCodes:
                            bd.id == data.id ? bd.useAsaCodes : !bd.useAsaCodes, // avoid useAsaCode change twice and remain as before
                        usingAmaRates: bd.useAsaCodes,
                    };
                else return { ...bd };
            });
    };

    const _syncBillingSelectorDetails = (data) => {
        patientEncounterDetails.billingData =
            patientEncounterDetails.billingData.map((bd) => {
                if (data.id) {
                    if (bd.id == data.id)
                        return {
                            ...bd,
                            billingType: Number(0),
                            calculationAmount: Number(0),
                            actualGap: Number(0),
                            totalFee: Number(0),
                            rebate: Number(0),
                            oopType: Number(0),
                            // withinFundRules: false,
                            // patientPaysTotalFee: false,
                        };
                    else return { ...bd };
                } else if (data.isLatestQuoteData) return { ...bd, ...data };
                else return { ...bd };
            });
    };

    const _handleUpdateBillingData = async (
        res,
        hasItemTypeChanged,
        updatedBillingData,
    ) => {
        if (!res)
            showError(
                `Failed to update fee estimate billing details, please refresh the page and try again and contact support if the issue persists.`,
            );
        else if (res.error) showError(res.error);
        else if (!associateArr || !associateArr.length) {
            if (hasItemTypeChanged)
                await reloadBillingDetailsAndUpdateDefaultItems();
            else if (isTimeItemAdded || isProcedureItemAdded)
                await reloadBillingItemsWhenTimeItemAdded(type);
            else if (res.id == patientEncounterDetails.id)
                await reloadBillingDetails();
        } else {
            if (
                associateArr.some(
                    (data) =>
                        data.patientEncounterBillingDataId ==
                        updatedBillingData.id,
                )
            )
                _openSyncAssociateQuoteOrInvoiceModal(
                    updatedBillingData,
                    hasItemTypeChanged,
                );
            else if (hasItemTypeChanged)
                await reloadBillingDetailsAndUpdateDefaultItems();
            else if (isTimeItemAdded || isProcedureItemAdded)
                await reloadBillingItemsWhenTimeItemAdded(type);
            else if (res.id == patientEncounterDetails.id)
                await reloadBillingDetails();
        }

        isTimeItemAdded = false;
        isProcedureItemAdded = false;
    };

    const _openEditDetailModal = () => (isEditDetailsModalOpen = true);

    const _closeEditDetailModal = () => {
        isEditDetailsModalOpen = false;
    };

    const _openSyncAssociateQuoteOrInvoiceModal = (
        data,
        hasItemTypeChanged,
    ) => {
        latestObjDetails = { ...data, hasItemTypeChanged };

        if (hasItemTypeChanged) {
            latestObjDetails.mbsData = [];
            latestObjDetails.asaData = [];
        }
        isSyncAssociateQuoteOrInvoiceModalOpen = true;
    };

    const _closeSyncAssociateQuoteOrInvoiceModal = () => {
        isSyncAssociateQuoteOrInvoiceModalOpen = false;

        if (latestObjDetails.hasItemTypeChanged)
            reloadBillingDetailsAndUpdateDefaultItems();
        else reloadBillingDetails();
    };

    const _openConfirmActionModal = (...args) => {
        // if (!scenarioOneFulfilled) {
        //     updateLatestDetails(...args);
        //     _closeConfirmActionModal();
        //     return;
        // }

        // isConfirmActionModalOpen = true;

        confirmAction = () => updateLatestDetails(...args);
    };

    const _closeConfirmActionModal = () => {
        isConfirmActionModalOpen = false;
        confirmAction = () => {};
    };
</script>

<CardLayout title="Fee Estimate Billing Details" {btnOptions}>
    {#if isObjectEmpty(latestObjDetails)}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>No fee estimate billing data</p>
            </div>
        </div>
    {:else}
        <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">OOP Type:</p>
                    <p class="content">
                        {getOOPType(latestObjDetails.oopType)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Patient Pays Total Fee:</p>
                    <p class="content">
                        {getBoolean(latestObjDetails.patientPaysTotalFee)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Within Fund Rules:</p>
                    <p class="content">
                        {getBoolean(latestObjDetails.withinFundRules)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Billing Type:</p>
                    <p class="content">
                        {getBillingType(latestObjDetails.billingType ?? 0)}
                    </p>
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Add GST:</p>
                    <p class="content">
                        {getBoolean(latestObjDetails.addGST)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Actual Gap:</p>
                    <p class="content">
                        {getAmountInCurrency(latestObjDetails.actualGap)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Rebate:</p>
                    <p class="content">
                        {getAmountInCurrency(latestObjDetails.rebate)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Total Fee:</p>
                    <p class="content">
                        {getAmountInCurrency(
                            latestObjDetails.addGST
                                ? latestObjDetails.totalFee * 1.1
                                : latestObjDetails.totalFee,
                        )}
                    </p>
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Start Date:</p>
                    <p class="content">
                        {getFormatDateAndTime(latestObjDetails.startDate)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">End Date:</p>
                    <p class="content">
                        {getFormatDateAndTime(latestObjDetails.endDate)}
                    </p>
                </div>
            </div>
        </div>
    {/if}
</CardLayout>

<ModifyBillingDetailsModal
    isModalOpen={isEditDetailsModalOpen}
    {latestObjDetails}
    {patientEncounterDetails}
    updateLatestDetails={confirmUpdateLatestDetails}
    {itemsData}
    closeModal={_closeEditDetailModal}
    {type}
    isUpdatingBillingDetails={isUpdatingDetails}
/>

<NotificationModal
    isModalOpen={isSyncAssociateQuoteOrInvoiceModalOpen}
    submitText="Confirm"
    closeModal={_closeSyncAssociateQuoteOrInvoiceModal}
    confirmAction={syncAssociateQuoteOrInvoice}
    isConfirming={isUpdatingAssociateQuoteOrInvoice}
    closeModalFunction={_closeSyncAssociateQuoteOrInvoiceModal}
>
    <p class="leading-6">
        Update successfully. Please note this change will NOT affect the
        existing estimate.
    </p>

    <p class="leading-6">
        Do you want to re-calculate the totals and re-generate the existing
        estimate and create & send a fee estimate of {getAmountInCurrency(
            latestObjDetails.totalFee,
        )} with a gap of {getAmountInCurrency(latestObjDetails.actualGap)} to the
        patient??
    </p>
</NotificationModal>

<style>
    .title {
        width: 200px;
    }

    .content {
        width: calc(100% - 200px);
    }
</style>
